import React, { Suspense, lazy } from 'react';
import './App.css';
import './style.css';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';

// ---------------------------------------------------------
// ⚡ STATIC IMPORTS (Jo har page pe chahiye ya wrappers hain)
// ---------------------------------------------------------
import ProtectedRoute from "./admin/ProtectedRoute";
import ProtectedRouteUser from './services/ProtectedRouteUser.jsx';
import DynamicSEO from './components/DynamicSEO.jsx';
import UsersList from './admin/UsersList.jsx';

// ---------------------------------------------------------
// 🚀 LAZY IMPORTS (User jab click karega tabhi JS download hogi)
// ---------------------------------------------------------
// User Pages
const Home = lazy(() => import('./pages/home.jsx'));
const Product = lazy(() => import('./pages/Products.jsx')); // Single Product page
const Products = lazy(() => import('./admin/Products.jsx')); // All products
const Cart = lazy(() => import('./pages/Cart.jsx'));
const Register = lazy(() => import('./pages/Register.jsx'));
const LoginUser = lazy(() => import('./pages/Login.jsx'));
const Profile = lazy(() => import('./pages/Profile.jsx'));
const Checkout = lazy(() => import('./pages/Checkout.jsx'));
const Orders = lazy(() => import('./pages/Orders.jsx'));
const Wishlist = lazy(() => import('./pages/Wishlist.jsx'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess.jsx'));

// Static/Info Pages
const Terms = lazy(() => import('./pages/Terms.jsx'));
const Refund = lazy(() => import('./pages/Refund.jsx'));
const Shipping = lazy(() => import('./pages/Shipping.jsx'));
const Disclaimer = lazy(() => import('./pages/Disclaimer.jsx'));
const Privacy = lazy(() => import('./pages/Privacy.jsx'));
const About = lazy(() => import('./pages/About.jsx'));
const Contact = lazy(() => import('./pages/ContactUs.jsx'));

// Blogs
const BlogLists = lazy(() => import('./pages/BlogLists.jsx'));
const BlogSingle = lazy(() => import('./pages/BlogSingle.jsx'));

// Admin Pages
const Login = lazy(() => import('./admin/Login.jsx'));
const Dashboard = lazy(() => import('./admin/Dashboard'));
const CategoryList = lazy(() => import('./admin/categoryList.jsx'));
const ProductList = lazy(() => import('./admin/ProductList.jsx'));
const ProductForm = lazy(() => import('./admin/ProductForm.jsx'));
const EditProduct = lazy(() => import('./admin/EditProduct.jsx'));
const CommentList = lazy(() => import('./admin/CommentList.jsx'));
const AdminOrders = lazy(() => import('./admin/AdminOrders.jsx'));
const AdminCoupons = lazy(() => import('./admin/AdminCoupons.jsx'));
const AdminReviews = lazy(() => import('./admin/AdminReviews.jsx'));
const AdminContact = lazy(() => import('./admin/AdminContact.jsx'));
const AdminBlogs = lazy(() => import('./admin/AdminBlogs.jsx'));
const AdminCreateBlog = lazy(() => import('./admin/AdminCreateBlog.jsx'));
const AdminEditBlog = lazy(() => import('./admin/AdminEditBlog.jsx'));
const AdminBanners = lazy(() => import('./admin/AdminBanners.jsx'));
const AdminCreateBanner = lazy(() => import('./admin/AdminCreateBanner.jsx'));
const AdminEditBanner = lazy(() => import('./admin/AdminEditBanner.jsx'));

// ---------------------------------------------------------
// 🎨 LOADING FALLBACK (Jab page load ho raha ho)
// ---------------------------------------------------------
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    {/* Aap yahan apna koi badhiya sa CSS spinner laga sakte ho */}
    <h3>Loading...</h3>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <DynamicSEO />
        
        {/* Suspense poori app ko wrap karega fallback ke saath */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* User Routes */}
            <Route path="/" element={<Home />} />
            <Route path="order-success" element={<OrderSuccess />} />
            <Route path="/product/:slug" element={<Product />} />
            <Route path="/products" element={<Products />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/login" element={<LoginUser />} />
            <Route path="/register" element={<Register />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/blogs" element={<BlogLists />} />
            <Route path="/blog/:slug" element={<BlogSingle />} />
            <Route path="/about-us" element={<About />} />
            <Route path="/contact-us" element={<Contact />} />
            
            {/* Protected User Routes */}
            <Route path="/profile" element={<ProtectedRouteUser><Profile /></ProtectedRouteUser>} />
            <Route path="/checkout" element={<ProtectedRouteUser><Checkout /></ProtectedRouteUser>} />

            {/* Policy Routes */}
            <Route path="/terms&condition" element={<Terms />} />
            <Route path="/refund&cancellation" element={<Refund />} />
            <Route path="/shipping-policy" element={<Shipping />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/privacy-policy" element={<Privacy />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<Login />} />
            
            <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><UsersList /></ProtectedRoute>} />
            <Route path="/admin/categories" element={<ProtectedRoute><CategoryList /></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute><ProductList /></ProtectedRoute>} />
            <Route path="/admin/products/create" element={<ProtectedRoute><ProductForm /></ProtectedRoute>} />
            <Route path="/admin/products/edit/:id" element={<ProtectedRoute><EditProduct /></ProtectedRoute>} />
            <Route path="/admin/comments" element={<ProtectedRoute><CommentList /></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute><AdminOrders /></ProtectedRoute>} />
            <Route path="/admin/coupons" element={<ProtectedRoute><AdminCoupons /></ProtectedRoute>} />
            <Route path="/admin/reviews" element={<ProtectedRoute><AdminReviews /></ProtectedRoute>} />
            <Route path="/admin/contacts" element={<ProtectedRoute><AdminContact /></ProtectedRoute>} />
            <Route path="/admin/blogs" element={<ProtectedRoute><AdminBlogs /></ProtectedRoute>} />
            <Route path="/admin/blog/create" element={<ProtectedRoute><AdminCreateBlog /></ProtectedRoute>} />
            <Route path="/admin/blog/edit/:id" element={<ProtectedRoute><AdminEditBlog /></ProtectedRoute>} />
            <Route path="/admin/banners" element={<ProtectedRoute><AdminBanners /></ProtectedRoute>} />
            <Route path="/admin/banner/create" element={<ProtectedRoute><AdminCreateBanner /></ProtectedRoute>} />
            <Route path="/admin/banner/edit/:id" element={<ProtectedRoute><AdminEditBanner /></ProtectedRoute>} />
            
          </Routes>
        </Suspense>

      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;